import { useEffect } from 'react';

/**
 * Suppresses ResizeObserver loop errors that commonly occur with Google Maps.
 * These errors are harmless but clutter the console.
 *
 * This hook intercepts both error events and console.error calls to prevent
 * ResizeObserver-related warnings from appearing in the console.
 *
 * @example
 * ```tsx
 * function MapComponent() {
 *   useSuppressResizeObserverErrors();
 *   return <Map>...</Map>;
 * }
 * ```
 */
export function useSuppressResizeObserverErrors(): void {
  useEffect(() => {
    // Suppress error events — must use capture phase so this fires before
    // CRA's dev overlay handler (which also listens on capture).
    const errorHandler = (e: ErrorEvent): boolean | void => {
      if (e.message && (
          e.message.includes('ResizeObserver loop') ||
          e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
          e.message === 'ResizeObserver loop limit exceeded'
      )) {
        e.stopImmediatePropagation();
        e.preventDefault();
        return false;
      }
    };

    // Also suppress console errors
    const originalConsoleError = console.error;
    console.error = (...args: unknown[]) => {
      const errorMessage = args[0]?.toString() || '';
      if (errorMessage.includes('ResizeObserver loop')) {
        return;
      }
      originalConsoleError.apply(console, args);
    };

    window.addEventListener('error', errorHandler, true);  // capture phase

    return () => {
      window.removeEventListener('error', errorHandler, true);
      console.error = originalConsoleError;
    };
  }, []);
}

